import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, ExternalLink, CheckCircle, XCircle, Clock, Download } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_PASSWORD = "render2024";

const AdminPage = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const navigate = useNavigate();

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setAuthenticated(true);
      localStorage.setItem("admin_auth", "true");
    } else {
      alert("Invalid password");
    }
  };

  useEffect(() => {
    if (localStorage.getItem("admin_auth") === "true") {
      setAuthenticated(true);
    }
  }, []);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/submissions?password=${ADMIN_PASSWORD}`);
      if (response.ok) {
        const data = await response.json();
        setSubmissions(data.submissions || []);
      }
    } catch (err) {
      console.error("Failed to fetch submissions:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authenticated) {
      fetchSubmissions();
    }
  }, [authenticated]);

  const getStatusDisplay = (sub) => {
    if (sub.status === 'complete' && sub.email_sent) {
      return { label: 'Delivered', color: '#16a34a', bg: '#dcfce7', icon: CheckCircle };
    } else if (sub.status === 'complete' && !sub.email_sent) {
      return { label: 'Rendered (not sent)', color: '#B85C20', bg: '#fef3e7', icon: XCircle };
    } else if (sub.status === 'failed') {
      return { label: 'Failed', color: '#dc2626', bg: '#fef2f2', icon: XCircle };
    } else if (sub.status === 'processing') {
      return { label: 'Processing', color: '#B85C20', bg: '#fef3e7', icon: Clock };
    } else {
      return { label: 'Pending', color: '#8A8070', bg: '#f5f5f4', icon: Clock };
    }
  };

  const downloadCSV = () => {
    if (submissions.length === 0) return;
    
    // CSV headers
    const headers = ['Email', 'Video Title', 'YouTube URL', 'Status', 'Email Delivered', 'Date'];
    
    // CSV rows
    const rows = submissions.map(sub => {
      const status = getStatusDisplay(sub);
      return [
        sub.email,
        sub.video_title || '',
        sub.youtube_url,
        sub.status,
        sub.email_sent ? 'Yes' : 'No',
        new Date(sub.created_at).toLocaleString()
      ].map(field => `"${String(field).replace(/"/g, '""')}"`).join(',');
    });
    
    const csv = [headers.join(','), ...rows].join('\n');
    
    // Create download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `render-submissions-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Login screen
  if (!authenticated) {
    return (
      <div style={{ background: '#F7F4EF', minHeight: '100vh' }}>
        <div style={{ maxWidth: '400px', margin: '0 auto', padding: '120px 24px' }}>
          <h1 style={{ 
            fontFamily: '"Playfair Display", Georgia, serif',
            fontSize: '32px',
            fontWeight: 700,
            fontStyle: 'italic',
            color: '#1A1814',
            textAlign: 'center',
            marginBottom: '40px'
          }}>
            Admin Access
          </h1>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{
                width: '100%',
                padding: '14px 16px',
                fontSize: '15px',
                color: '#1A1814',
                background: '#FFFFFF',
                border: '0.5px solid #D8D2C8',
                borderRadius: 0,
                outline: 'none',
                fontFamily: 'Inter, system-ui, sans-serif',
                boxSizing: 'border-box',
                marginBottom: '12px'
              }}
            />
            <button
              type="submit"
              style={{
                width: '100%',
                padding: '14px 24px',
                fontSize: '15px',
                fontWeight: 500,
                color: '#F7F4EF',
                background: '#1A1814',
                border: 'none',
                borderRadius: 0,
                cursor: 'pointer',
                fontFamily: 'Inter, system-ui, sans-serif'
              }}
            >
              Enter
            </button>
          </form>
          <button
            onClick={() => navigate("/")}
            style={{
              width: '100%',
              marginTop: '24px',
              fontSize: '13px',
              color: '#8A8070',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'Inter, system-ui, sans-serif'
            }}
          >
            ← Back to home
          </button>
        </div>
      </div>
    );
  }

  // Detail view
  if (selectedSubmission) {
    const status = getStatusDisplay(selectedSubmission);
    const StatusIcon = status.icon;
    
    return (
      <div style={{ background: '#F7F4EF', minHeight: '100vh' }}>
        <div style={{ maxWidth: '720px', margin: '0 auto', padding: '36px 48px' }}>
          <header style={{ marginBottom: '48px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button
              onClick={() => setSelectedSubmission(null)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '13px',
                color: '#1A1814',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'Inter, system-ui, sans-serif'
              }}
            >
              <ArrowLeft size={16} /> Back to list
            </button>
            <span style={{ fontSize: '11px', fontWeight: 500, letterSpacing: '0.14em', color: '#B85C20', fontFamily: 'Inter, system-ui, sans-serif' }}>
              RENDER ADMIN
            </span>
          </header>

          <div style={{ background: '#FFFFFF', border: '0.5px solid #D8D2C8', padding: '32px 40px', marginBottom: '24px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              <div>
                <p style={{ fontSize: '10px', fontWeight: 500, letterSpacing: '0.14em', color: '#8A8070', marginBottom: '6px', fontFamily: 'Inter, system-ui, sans-serif' }}>EMAIL</p>
                <p style={{ fontSize: '15px', color: '#1A1814', fontFamily: 'Inter, system-ui, sans-serif' }}>{selectedSubmission.email}</p>
              </div>
              <div>
                <p style={{ fontSize: '10px', fontWeight: 500, letterSpacing: '0.14em', color: '#8A8070', marginBottom: '6px', fontFamily: 'Inter, system-ui, sans-serif' }}>STATUS</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <StatusIcon size={14} style={{ color: status.color }} />
                  <span style={{ fontSize: '14px', fontWeight: 500, color: status.color, fontFamily: 'Inter, system-ui, sans-serif' }}>{status.label}</span>
                </div>
              </div>
              <div>
                <p style={{ fontSize: '10px', fontWeight: 500, letterSpacing: '0.14em', color: '#8A8070', marginBottom: '6px', fontFamily: 'Inter, system-ui, sans-serif' }}>VIDEO TITLE</p>
                <p style={{ fontSize: '15px', color: '#1A1814', fontFamily: 'Inter, system-ui, sans-serif' }}>{selectedSubmission.video_title || 'Processing...'}</p>
              </div>
              <div>
                <p style={{ fontSize: '10px', fontWeight: 500, letterSpacing: '0.14em', color: '#8A8070', marginBottom: '6px', fontFamily: 'Inter, system-ui, sans-serif' }}>SUBMITTED</p>
                <p style={{ fontSize: '15px', color: '#1A1814', fontFamily: 'Inter, system-ui, sans-serif' }}>
                  {new Date(selectedSubmission.created_at).toLocaleString()}
                </p>
              </div>
            </div>
            
            <hr style={{ border: 'none', borderTop: '1px solid #E8E4DC', margin: '24px 0' }} />
            
            <div>
              <p style={{ fontSize: '10px', fontWeight: 500, letterSpacing: '0.14em', color: '#8A8070', marginBottom: '6px', fontFamily: 'Inter, system-ui, sans-serif' }}>YOUTUBE URL</p>
              <a 
                href={selectedSubmission.youtube_url} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ fontSize: '14px', color: '#B85C20', fontFamily: 'Inter, system-ui, sans-serif', display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                {selectedSubmission.youtube_url} <ExternalLink size={14} />
              </a>
            </div>
          </div>

          {selectedSubmission.render_output && (
            <div style={{ background: '#FFFFFF', border: '0.5px solid #D8D2C8', padding: '32px 40px' }}>
              <p style={{ fontSize: '10px', fontWeight: 500, letterSpacing: '0.14em', color: '#B85C20', marginBottom: '16px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                RENDER OUTPUT
              </p>
              <pre style={{ 
                fontSize: '14px', 
                color: '#1A1814', 
                lineHeight: 1.65,
                whiteSpace: 'pre-wrap',
                fontFamily: 'Inter, system-ui, sans-serif',
                margin: 0
              }}>
                {selectedSubmission.render_output}
              </pre>
            </div>
          )}
        </div>
      </div>
    );
  }

  // List view
  return (
    <div style={{ background: '#F7F4EF', minHeight: '100vh' }}>
      <div style={{ maxWidth: '960px', margin: '0 auto', padding: '36px 48px' }}>
        <header style={{ marginBottom: '48px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '11px', fontWeight: 500, letterSpacing: '0.14em', color: '#1A1814', fontFamily: 'Inter, system-ui, sans-serif' }}>
            RENDER ADMIN
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
            <button
              onClick={downloadCSV}
              disabled={submissions.length === 0}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '13px',
                color: submissions.length === 0 ? '#D8D2C8' : '#1A1814',
                background: 'none',
                border: '0.5px solid #D8D2C8',
                padding: '8px 14px',
                cursor: submissions.length === 0 ? 'default' : 'pointer',
                fontFamily: 'Inter, system-ui, sans-serif'
              }}
            >
              <Download size={14} /> Export CSV
            </button>
            <button
              onClick={fetchSubmissions}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '13px',
                color: '#8A8070',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'Inter, system-ui, sans-serif'
              }}
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
            <button
              onClick={() => {
                localStorage.removeItem("admin_auth");
                setAuthenticated(false);
              }}
              style={{
                fontSize: '13px',
                color: '#8A8070',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'Inter, system-ui, sans-serif'
              }}
            >
              Logout
            </button>
          </div>
        </header>

        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ 
            fontFamily: '"Playfair Display", Georgia, serif',
            fontSize: '36px',
            fontWeight: 700,
            fontStyle: 'italic',
            color: '#1A1814',
            marginBottom: '8px'
          }}>
            Submissions
          </h1>
          <p style={{ fontSize: '14px', color: '#8A8070', fontFamily: 'Inter, system-ui, sans-serif' }}>
            {submissions.length} total
          </p>
        </div>

        <div style={{ background: '#FFFFFF', border: '0.5px solid #D8D2C8' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #E8E4DC' }}>
                <th style={{ textAlign: 'left', fontSize: '10px', fontWeight: 500, letterSpacing: '0.14em', color: '#8A8070', padding: '14px 20px', fontFamily: 'Inter, system-ui, sans-serif' }}>EMAIL</th>
                <th style={{ textAlign: 'left', fontSize: '10px', fontWeight: 500, letterSpacing: '0.14em', color: '#8A8070', padding: '14px 20px', fontFamily: 'Inter, system-ui, sans-serif' }}>VIDEO</th>
                <th style={{ textAlign: 'left', fontSize: '10px', fontWeight: 500, letterSpacing: '0.14em', color: '#8A8070', padding: '14px 20px', fontFamily: 'Inter, system-ui, sans-serif' }}>STATUS</th>
                <th style={{ textAlign: 'left', fontSize: '10px', fontWeight: 500, letterSpacing: '0.14em', color: '#8A8070', padding: '14px 20px', fontFamily: 'Inter, system-ui, sans-serif' }}>DATE</th>
                <th style={{ padding: '14px 20px' }}></th>
              </tr>
            </thead>
            <tbody>
              {submissions.map((sub) => {
                const status = getStatusDisplay(sub);
                const StatusIcon = status.icon;
                return (
                  <tr key={sub.id} style={{ borderBottom: '1px solid #E8E4DC' }}>
                    <td style={{ padding: '16px 20px', fontSize: '14px', color: '#1A1814', fontFamily: 'Inter, system-ui, sans-serif' }}>
                      {sub.email}
                    </td>
                    <td style={{ padding: '16px 20px', fontSize: '14px', color: '#1A1814', fontFamily: 'Inter, system-ui, sans-serif', maxWidth: '200px' }}>
                      {sub.video_title ? (sub.video_title.length > 35 ? sub.video_title.substring(0, 35) + '...' : sub.video_title) : '-'}
                    </td>
                    <td style={{ padding: '16px 20px' }}>
                      <span style={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '12px',
                        fontWeight: 500,
                        padding: '4px 10px',
                        background: status.bg,
                        color: status.color,
                        fontFamily: 'Inter, system-ui, sans-serif'
                      }}>
                        <StatusIcon size={12} />
                        {status.label}
                      </span>
                    </td>
                    <td style={{ padding: '16px 20px', fontSize: '13px', color: '#8A8070', fontFamily: 'Inter, system-ui, sans-serif' }}>
                      {new Date(sub.created_at).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '16px 20px' }}>
                      <button
                        onClick={() => setSelectedSubmission(sub)}
                        style={{
                          fontSize: '13px',
                          fontWeight: 500,
                          color: '#B85C20',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontFamily: 'Inter, system-ui, sans-serif'
                        }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
              {submissions.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ padding: '40px 20px', textAlign: 'center', fontSize: '14px', color: '#8A8070', fontFamily: 'Inter, system-ui, sans-serif' }}>
                    No submissions yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
